<template>
  <div class="gantt-container" ref="ganttContainer" @wheel="handleWheel"
    :style="containerHeight ? { height: containerHeight + 'px' } : null">
    <!-- Activity search -->
    <div class="gantt-search-bar" v-if="!isLoading">
      <div class="gantt-search-inner">
        <label class="gantt-search-label">
          <i class="fa fa-search"></i>
          {{ $t('gantt_search_activity') }}
        </label>
        <b-field class="gantt-search-field">
          <b-autocomplete
            v-model="activitySearchQuery"
            :data="filteredSearchActivities"
            :placeholder="$t('gantt_search_activity_by_code_or_name')"
            field="searchLabel"
            icon="search"
            icon-pack="fas"
            rounded
            expanded
            :open-on-focus="false"
            :clearable="true"
            :disabled="!searchableActivities.length"
            @select="onActivitySearchSelect"
          >
            <template slot-scope="props">
              <div class="gantt-search-option">
                <span class="gantt-search-code">{{ props.option.code }}</span>
                <span class="gantt-search-name">{{ props.option.name }}</span>
              </div>
            </template>
            <template #empty>{{ $t('no_results') }}</template>
          </b-autocomplete>
        </b-field>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="gantt-toolbar">
      <div class="toolbar-group">
        <!-- Views/Baselines dropdown -->
        <div class="toolbar-dropdown">
          <button class="toolbar-btn btn-primary" @click.stop="toggleViewsMenu($event)">
            <i class="fa fa-bars-staggered"></i> {{ $t('views') }}
          </button>
          <div v-if="showViewsMenu" class="gantt-dropdown-menu" @click.stop
            :style="{ position: 'fixed', left: viewsMenuPosition.x + 'px', top: viewsMenuPosition.y + 'px' }">
            <div class="dropdown-item" v-for="(config, key) in baselinesConfig" :key="key"
              v-show="!(key === 'baseline-2' && isInspected)">
              <label>
                <input type="checkbox" v-model="config.visible" />
                {{ $t(config.labelKey) }}
              </label>
            </div>
          </div>
        </div>

        <!-- Dependencies toggle -->
        <button class="toolbar-btn" :class="enableDependencies ? 'btn-success-active' : 'btn-success'"
          @click="enableDependencies = !enableDependencies">
          <i class="fa fa-project-diagram"></i> {{ $t('dependencies') }}
        </button>

        <!-- Critical path toggle + dropdown filtros -->
        <div class="toolbar-dropdown critical-path-dropdown">
          <button class="toolbar-btn critical-path-btn" :class="enableCriticalPaths ? 'btn-danger-active' : 'btn-danger'"
            @click="toggleCriticalPaths">
            <i class="fa fa-fire"></i> {{ $t('critical_path_gantt') }}
          </button><button class="toolbar-btn critical-path-caret" :class="enableCriticalPaths ? 'btn-danger-active' : 'btn-danger'"
            :disabled="!enableCriticalPaths"
            @click.stop="enableCriticalPaths && (showCriticalMenu = !showCriticalMenu)">
            <i class="fa fa-caret-down"></i>
          </button>
          <div v-if="showCriticalMenu" class="gantt-dropdown-menu critical-dropdown-menu" @click.stop
            :style="{ position: 'absolute', top: '100%', left: 0, marginTop: '2px' }">
            <div class="dropdown-item" v-if="company.alias !== 'elecnor1'">
              <label>
                <input type="checkbox" :checked="filterLongestPath"
                  @change="setCriticalFilter('longestPath')" />
                {{ $t('filter_longest_path') }}
              </label>
            </div>
            <div class="dropdown-item">
              <label>
                <input type="checkbox" :checked="filterCriticalFlat"
                  @change="setCriticalFilter('flat')" />
                {{ $t('filter_critical_flat') }}
              </label>
            </div>
          </div>
        </div>

        <!-- Zoom controls -->
        <button class="toolbar-btn btn-primary" @click="zoomIn" :disabled="zoomLevel >= zoomLevels.length - 1">
          <i class="fa fa-search-plus"></i>
        </button>
        <button class="toolbar-btn btn-primary" @click="zoomOut" :disabled="zoomLevel <= 0">
          <i class="fa fa-search-minus"></i>
        </button>
        <button class="toolbar-btn btn-primary" @click="zoomToFit" :title="$t('fit_content')">
          <i class="fa fa-compress-arrows-alt"></i>
        </button>

        <!-- Fullscreen -->
        <button class="toolbar-btn btn-primary" @click="toggleFullscreen" :title="$t('fullscreen')">
          <i class="fa fa-expand-arrows-alt"></i>
        </button>

        <!-- Screenshot -->
        <button class="toolbar-btn btn-primary" @click="captureScreenshot" :disabled="isCapturing"
          :title="$t('screenshot_gantt')">
          <i :class="isCapturing ? 'fa fa-spinner fa-spin' : 'fa fa-camera'"></i>
        </button>

        <!-- Export Excel -->
        <button class="toolbar-btn btn-primary" @click="exportExcel" :disabled="isExporting"
          :title="$t('gantt_export_excel')">
          <i :class="isExporting ? 'fa fa-spinner fa-spin' : 'fa fa-file-excel'"></i>
        </button>

        <!-- Info -->
        <div class="toolbar-info" @mouseenter="showInfoTooltip = true" @mouseleave="showInfoTooltip = false">
          <i class="fa fa-info"></i>
          <div v-if="showInfoTooltip" class="info-tooltip">
            <span class="info-bar gray">{{ $t('not_started') }}</span>
            <span class="info-bar blue">{{ $t('with_real_dates') }}</span>
            <div class="info-levels">
              <div class="info-level">
                <span class="level-badge level-badge-group" :style="{ '--group-color': colors.defaults.group }">EN</span>{{ $t('group') }}
              </div>
              <div class="info-level">
                <span class="level-badge level-badge-activity">{{ $t('gantt_level_activity_short') }}</span>{{ $t('activity') }}
              </div>
              <div class="info-level">
                <span class="level-badge level-badge-subactivity">{{ $t('gantt_level_subactivity_short') }}</span>{{ $t('subactivity') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="gantt-main" v-if="!isLoading">
      <!-- Left Panel (Table) -->
      <div class="gantt-left-panel" ref="leftPanel" :style="{ width: leftPanelWidth + 'px' }">
        <!-- Table Header Wrapper - syncs horizontal scroll -->
        <div class="gantt-table-header-wrapper" ref="tableHeaderWrapper" :style="{ height: timeHeaderHeight + 'px' }">
          <div class="gantt-table-header" ref="tableHeader" :style="{ height: '100%' }">
            <div class="gantt-table-header-row" ref="headerRow" :style="{ minWidth: totalColumnsWidth + 'px' }">
              <div v-for="(col, idx) in visibleColumns" :key="col.field" class="gantt-table-header-cell"
                :class="{
                  'header-centered': isCenteredColumn(col),
                  'column-dragging-source': columnDragging.active && columnDragging.hasMoved && columnDragging.sourceIndex === idx,
                  'column-drop-left': columnDragging.active && columnDragging.hasMoved && columnDragging.targetIndex === idx && columnDragging.sourceIndex > idx,
                  'column-drop-right': columnDragging.active && columnDragging.hasMoved && columnDragging.targetIndex === idx && columnDragging.sourceIndex < idx
                }"
                :style="getColumnStyle(col, idx)"
                @contextmenu.prevent="showColumnContextMenu($event, idx)"
                @click="handleColumnClick($event, col.field)"
                @mousedown="startColumnDrag($event, idx)">
                <i class="fa fa-grip-vertical column-drag-handle"></i>
                <span class="header-text">{{ $t(col.textKey) || col.text }}</span>
                <span class="sort-icon" :class="{ active: sortField === col.field }">
                  <i class="fa fa-sort" v-if="sortField !== col.field"></i>
                  <i class="fa fa-sort-up" v-else-if="sortDirection === 'asc'"></i>
                  <i class="fa fa-sort-down" v-else></i>
                </span>
                <div class="column-resize-handle" @mousedown.stop="startColumnResize($event, idx)" @click.stop></div>
              </div>
            </div>
          </div>
        </div>
        <!-- Table Body -->
        <div class="gantt-table-body" ref="leftBody" @scroll="syncScroll('left')" @mousedown="startLeftPan">
          <div class="gantt-table-content"
            :style="{ height: totalRowsHeight + 'px', minWidth: totalColumnsWidth + 'px' }">
            <!-- Filas padre fijas -->
            <div v-if="stickyRows.length" class="gantt-sticky-rows">
              <div v-for="(row, stickyIdx) in stickyRows" :key="'sticky-' + row.id"
                class="gantt-table-row is-sticky" :class="{
                  'is-group': row.type === 'group',
                  'is-open-activity': isOpenActivity(row),
                  'is-last-sticky': stickyIdx === stickyRows.length - 1,
                  'is-selected': selectedRowId === row.id
                }"
                :style="{ height: rowHeight + 'px', top: stickyIdx * rowHeight + 'px', minWidth: totalColumnsWidth + 'px', '--group-color': row.groupColor }"
                @click="selectRow(row); scrollToTask(row)">
                <div v-for="col in visibleColumns" :key="col.field" class="gantt-table-cell"
                  :class="{ 'cell-centered': isCenteredColumn(col) }"
                  :style="{ width: getEffectiveColumnWidth(col) + 'px', minWidth: col.minWidth + 'px' }"
                  :title="getCellTitle(row, col)">
                  <template v-if="col.isTree">
                    <span class="tree-content" :style="{ paddingLeft: (row.level * 16) + 'px' }">
                      <span class="expand-icon" @click.stop="toggleExpand(row)">
                        <i :class="row.expanded ? 'fa fa-caret-down' : 'fa fa-caret-right'"></i>
                      </span>
                      <span v-if="row.kind === 'group'" class="level-badge level-badge-group" :title="$t('group') + ': ' + row.name"
                        :style="{ color: row.groupTextColor }">
                        {{ row.groupInitials }}
                      </span>
                      <span v-else-if="row.kind" class="level-badge" :class="'level-badge-' + row.kind" :title="$t(row.kind)">
                        {{ $t('gantt_level_' + row.kind + '_short') }}
                      </span>
                      <span class="cell-text">{{ row[col.field] }}</span>
                    </span>
                  </template>
                  <template v-else-if="col.field === 'percentDone'">
                    <span class="cell-text">{{ formatPercent(row.percentDone) }}</span>
                  </template>
                  <template v-else-if="col.field === 'latestPlanningProgress'">
                    <span class="cell-text">{{ formatPercent(getLatestPlanningProgress(row)) }}</span>
                  </template>
                  <template v-else-if="col.field === 'duration'">
                    <span class="cell-text">{{ calculateDuration(row) }}</span>
                  </template>
                  <template v-else-if="col.field === 'status'">
                    <span v-if="row.status !== null && row.status !== undefined" class="status-badge"
                      :style="{ background: getStatusColor(row) }">
                      {{ getStatusLabel(row) }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="cell-text">{{ getCellTitle(row, col) }}</span>
                  </template>
                </div>
              </div>
            </div>
            <div v-for="(row, rowIdx) in renderedRows" :key="row.id" class="gantt-table-row" :class="{
              'is-group': row.type === 'group',
              'is-assignment': row.type === 'assignment',
              'is-subactivity': row.kind === 'subactivity',
              'is-open-activity': isOpenActivity(row),
              'is-selected': selectedRowId === row.id
            }"
              :style="{ height: rowHeight + 'px', top: (renderStartIndex + rowIdx) * rowHeight + 'px', minWidth: totalColumnsWidth + 'px', '--group-color': row.groupColor }"
              @click="selectRow(row)">
              <div v-for="col in visibleColumns" :key="col.field" class="gantt-table-cell"
                :class="{
                  'cell-centered': isCenteredColumn(col)
                }"
                :style="{ width: getEffectiveColumnWidth(col) + 'px', minWidth: col.minWidth + 'px' }"
                :title="getCellTitle(row, col)">
                <!-- Tree column with expand/collapse -->
                <template v-if="col.isTree">
                  <span class="tree-content" :style="{ paddingLeft: ((filterCriticalFlat || filterLongestPath ? 0 : row.level) * 16) + 'px' }">
                    <span v-if="row.children && row.children.length" class="expand-icon"
                      @click.stop="toggleExpand(row)">
                      <i :class="row.expanded ? 'fa fa-caret-down' : 'fa fa-caret-right'"></i>
                    </span>
                    <!-- Conector de subactividad -->
                    <span v-else-if="row.kind === 'subactivity' && !filterCriticalFlat && !filterLongestPath" class="tree-connector"></span>
                    <span v-else class="expand-placeholder"></span>
                    <!-- Badge de nivel -->
                    <span v-if="row.kind === 'group'" class="level-badge level-badge-group" :title="$t('group') + ': ' + row.name"
                        :style="{ color: row.groupTextColor }">
                      {{ row.groupInitials }}
                    </span>
                    <span v-else-if="row.kind" class="level-badge" :class="'level-badge-' + row.kind" :title="$t(row.kind)">
                      {{ $t('gantt_level_' + row.kind + '_short') }}
                    </span>
                    <span class="cell-text">{{ row[col.field] }}</span>
                  </span>
                </template>

                <!-- Status column -->
                <template v-else-if="col.field === 'status'">
                  <span v-if="row.status !== null && row.status !== undefined" class="status-badge"
                    :style="{ background: getStatusColor(row) }">
                    {{ getStatusLabel(row) }}
                  </span>
                </template>

                <!-- Progress column -->
                <template v-else-if="col.field === 'percentDone'">
                  <div class="progress-circle">
                    <svg viewBox="0 0 36 36">
                      <path class="progress-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                      <path class="progress-bar" :stroke-dasharray="(row.percentDone || 0) + ', 100'"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    </svg>
                    <span class="progress-text">{{ Math.round(row.percentDone || 0) }}%</span>
                  </div>
                </template>

                <!-- Latest planning progress column -->
                <template v-else-if="col.field === 'latestPlanningProgress'">
                  <div class="progress-circle">
                    <svg viewBox="0 0 36 36">
                      <path class="progress-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                      <path class="progress-bar" :stroke-dasharray="getLatestPlanningProgress(row) + ', 100'"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    </svg>
                    <span class="progress-text">{{ Math.round(getLatestPlanningProgress(row)) }}%</span>
                  </div>
                </template>

                <!-- Duration column -->
                <template v-else-if="col.field === 'duration'">
                  <span class="cell-text">{{ calculateDuration(row) }}</span>
                </template>

                <!-- Successors column -->
                <template v-else-if="col.field === 'successors'">
                  <div class="dependency-badges">
                    <span v-for="dep in getSuccessors(row.id)" :key="dep.id" class="dependency-badge"
                      @click.stop="goToTask(dep.to)" @mouseenter="showDependencyTooltip($event, dep)"
                      @mouseleave="hideTooltip">
                      {{ getTaskCode(dep.to) }} | {{ dependencyTypes[dep.type] }}
                      <template v-if="dep.lag !== 0">
                        {{ dep.lag > 0 ? '+' : '' }}{{ dep.lag + 1 }}{{ dep.lagUnit }}
                      </template>
                    </span>
                  </div>
                </template>

                <!-- Predecessors column -->
                <template v-else-if="col.field === 'predecessors'">
                  <div class="dependency-badges">
                    <span v-for="dep in getPredecessors(row.id)" :key="dep.id" class="dependency-badge"
                      @click.stop="goToTask(dep.from)" @mouseenter="showDependencyTooltip($event, dep)"
                      @mouseleave="hideTooltip">
                      {{ getTaskCode(dep.from) }} | {{ dependencyTypes[dep.type] }}
                      <template v-if="dep.lag !== 0">
                        {{ dep.lag > 0 ? '+' : '' }}{{ dep.lag + 1 }}{{ dep.lagUnit }}
                      </template>
                    </span>
                  </div>
                </template>

                <!-- Name column (clickable for activities) -->
                <template v-else-if="col.field === 'name'">
                  <a v-if="row.type === 'activity'" class="cell-text activity-link"
                    :href="getActivityUrl(row)" target="_blank"
                    @click.stop>{{ row.name }}</a>
                  <span v-else class="cell-text">{{ row.name }}</span>
                </template>

                <!-- Weight column (groups and leaf activities only) -->
                <template v-else-if="col.field === 'weight'">
                  <span class="cell-text">{{ formatWeight(row) }}</span>
                </template>

                <!-- Date columns -->
                <template v-else-if="col.field.includes('date') || col.field.includes('Date')">
                  <span class="cell-text">{{ formatDate(row[col.field]) }}</span>
                </template>

                <!-- Default -->
                <template v-else>
                  <span class="cell-text">{{ row[col.field] }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Splitter -->
      <div class="gantt-splitter" @mousedown="startSplitterDrag">
        <div class="splitter-handle"></div>
      </div>

      <!-- Right Panel (Timeline) -->
      <div class="gantt-right-panel" ref="rightPanel">
        <!-- Time Header -->
        <div class="gantt-time-header-wrapper" ref="timeHeaderWrapper" :style="{ height: timeHeaderHeight + 'px' }">
          <div class="gantt-time-header" ref="timeHeader" :style="{ width: totalTimelineWidth + 'px' }">
            <div v-for="(headerRow, idx) in timeHeaders" :key="idx" class="gantt-time-header-row"
              :class="[headerRow.unit, { compact: timeHeaders.length > 2 }]">
              <div v-for="cell in headerRow.cells" :key="cell.x" class="gantt-time-header-cell"
                :style="{ width: cell.width + 'px', left: cell.x + 'px' }">
                <span :class="{ 'sticky-label': headerRow.unit === 'year' }"
                  :style="getStickyLabelStyle(cell, headerRow.unit === 'year')">{{ cell.label }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Floating Now Label (outside scrollable body to avoid clipping) -->
        <div v-if="nowLabelPosition.isVisible" class="floating-now-label"
          :style="{ left: nowLabelPosition.left + 'px' }">
          {{ $t('now') }}
        </div>

        <!-- Chart Body -->
        <div class="gantt-chart-body" ref="rightBody" @scroll="syncScroll('right')" @mousedown="startPan">
          <div class="gantt-chart-content"
            :style="{ width: totalTimelineWidth + 'px', height: totalRowsHeight + 'px' }">
            <!-- Barras de las filas padre fijas -->
            <div v-if="stickyRows.length" class="gantt-sticky-rows" :style="{ width: totalTimelineWidth + 'px' }">
              <template v-for="(row, stickyIdx) in stickyRows">
                <div :key="'sticky-stripe-' + row.id" class="gantt-row-stripe is-sticky" :class="{
                  'is-group': row.type === 'group',
                  'is-last-sticky': stickyIdx === stickyRows.length - 1
                }"
                  :style="{ top: stickyIdx * rowHeight + 'px', height: rowHeight + 'px' }"></div>
                <div v-if="baselinesConfig['main-bar'].visible" :key="'sticky-bar-' + row.id" class="gantt-task-bar"
                  :class="{
                    'is-diamond': isDiamond(row),
                    'is-critical': row.isCriticalPath && enableCriticalPaths,
                    'is-parent': true
                  }" :style="getTaskBarStyle(row, stickyIdx)"
                  @mouseenter="showTaskTooltip($event, row)" @mouseleave="hideTooltip"
                  @click="selectRow(row); scrollToTask(row)">
                  <div v-if="!isDiamond(row)" class="task-progress-bar" :style="{ width: (row.percentDone || 0) + '%' }"></div>
                  <span v-if="!isLabelOutside(row)" class="task-label">{{ formatPercent(row.percentDone) }}</span>
                </div>
                <span v-if="baselinesConfig['main-bar'].visible && isLabelOutside(row)" :key="'sticky-label-' + row.id"
                  class="task-label-outside" :style="getOutsideLabelStyle(row, stickyIdx)">
                  {{ formatPercent(row.percentDone) }}
                </span>
              </template>
              <div class="gantt-current-time-line"
                :style="{ left: currentTimeX + 'px', top: 0, bottom: 'auto', height: stickyRows.length * rowHeight + 'px' }"></div>
            </div>
            <!-- Grid Background -->
            <div class="gantt-grid-background">
              <!-- Vertical grid lines -->
              <div v-for="(line, idx) in gridLines" :key="'v-' + idx" class="gantt-grid-line vertical"
                :class="{ major: line.isMajor }" :style="{ left: line.x + 'px' }"></div>
              <!-- Horizontal grid lines (row separators) -->
              <div v-for="(row, idx) in renderedRows" :key="'h-' + row.id" class="gantt-grid-line horizontal"
                :style="{ top: (renderStartIndex + idx + 1) * rowHeight + 'px' }"></div>
              <!-- Row backgrounds (only groups are highlighted) -->
              <div v-for="(row, idx) in renderedRows" :key="'stripe-' + row.id" class="gantt-row-stripe"
                :class="{ 'is-group': row.type === 'group' }"
                :style="{ top: (renderStartIndex + idx) * rowHeight + 'px', height: rowHeight + 'px' }"></div>
            </div>

            <!-- Current Time Line -->
            <div class="gantt-current-time-line" :style="{ left: currentTimeX + 'px' }"></div>

            <!-- SVG Dependencies Layer (behind bars) — tamaño limitado al viewport para evitar surface gigante -->
            <svg v-if="enableDependencies" class="gantt-dependencies-svg"
              :width="totalTimelineWidth"
              :height="(renderEndIndex - renderStartIndex + 1) * rowHeight"
              :style="{ top: renderStartIndex * rowHeight + 'px' }"
              shape-rendering="geometricPrecision">
              <defs>
                <marker id="arrowhead" markerWidth="7" markerHeight="6" refX="6" refY="3" orient="auto">
                  <polygon points="0 0, 7 3, 0 6" fill="#8b939e" />
                </marker>
              </defs>
              <g class="dependency-lines" :transform="'translate(0,' + (-renderStartIndex * rowHeight) + ')'">
                <g v-for="dep in dependencyPaths" :key="dep.id" class="dependency-group"
                  @mouseenter="showDependencyTooltip($event, dep)" @mouseleave="hideTooltip">
                  <!-- Invisible wider path for easier hover -->
                  <path :d="dep.path" stroke="transparent" stroke-width="12" fill="none"
                    class="dependency-path-hover" />
                  <!-- Visible path -->
                  <path :d="dep.path" stroke="#8b939e" stroke-width="1" fill="none" marker-end="url(#arrowhead)"
                    class="dependency-path" />
                </g>
              </g>
            </svg>

            <!-- Task Bars Container -->
            <div class="gantt-bars-container">
              <template v-for="(row, rowIdx) in renderedRows">
                <!-- Main Task Bar -->
                <div v-if="baselinesConfig['main-bar'].visible" :key="'bar-' + row.id" class="gantt-task-bar" :class="{
                  'is-diamond': isDiamond(row),
                  'is-critical': row.isCriticalPath && enableCriticalPaths,
                  'is-parent': row.children && row.children.length > 0
                }" :style="getTaskBarStyle(row, renderStartIndex + rowIdx)" @mouseenter="showTaskTooltip($event, row)"
                  @mouseleave="hideTooltip" @click="selectRow(row)">
                  <div v-if="!isDiamond(row)" class="task-progress-bar" :style="{ width: (row.percentDone || 0) + '%' }"></div>
                  <span v-if="!isLabelOutside(row)" class="task-label">{{ formatPercent(row.percentDone) }}</span>
                </div>
                <!-- Outside label for diamonds and small bars -->
                <span v-if="baselinesConfig['main-bar'].visible && isLabelOutside(row)"
                  :key="'label-' + row.id"
                  class="task-label-outside"
                  :style="getOutsideLabelStyle(row, renderStartIndex + rowIdx)">
                  {{ formatPercent(row.percentDone) }}
                </span>

                <!-- Baselines (adjacent to each other below main bar) -->
                <template v-for="(baseline, bIdx) in getVisibleBaselines(row)">
                  <div v-if="baseline.startDate" :key="'baseline-' + row.id + '-' + baseline.index"
                    class="gantt-baseline-bar" :class="'baseline-' + baseline.index"
                    :style="getBaselineBarStyle(row, renderStartIndex + rowIdx, baseline, bIdx)"
                    @mouseenter="showBaselineTooltip($event, row, baseline)" @mouseleave="hideTooltip">
                    <div class="baseline-progress-bar" :style="{ width: (baseline.progress || 0) + '%' }"></div>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-else class="gantt-loading">
      <i class="fa fa-spinner fa-spin"></i>&nbsp; {{ $t('loading') }}...
    </div>

    <!-- Tooltip -->
    <div v-if="tooltip.visible" class="gantt-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
      <!-- Task Tooltip -->
      <template v-if="tooltip.type === 'task'">
        <div class="tooltip-title">{{ tooltip.data.name }}</div>
        <div v-if="tooltip.data.kind" class="tooltip-level"
          :style="{ '--group-color': tooltip.data.groupColor }">{{ $t(tooltip.data.kind) }}</div>
        <div><b>{{ $t('progress') }}:</b> {{ formatPercent(tooltip.data.percentDone) }}</div>
        <div><b>{{ $t('expected_real_start_date') }}:</b> {{ formatDate(tooltip.data.startDate) }}</div>
        <div><b>{{ $t('expected_real_end_date') }}:</b> {{ formatDate(tooltip.data.endDate) }}</div>
        <div v-if="tooltip.data.gantt_real_start_date">
          <b>{{ $t('real_start_date') }}:</b> {{ formatDate(tooltip.data.gantt_real_start_date) }}
        </div>
        <div v-if="tooltip.data.gantt_real_end_date">
          <b>{{ $t('real_end_date') }}:</b> {{ formatDate(tooltip.data.gantt_real_end_date) }}
        </div>
        <!-- Secciones de líneas base activas -->
        <template v-for="baseline in getVisibleBaselines(tooltip.data)">
          <div :key="'tooltip-bl-' + baseline.index" class="tooltip-baseline-section">
            <div class="tooltip-baseline-title">{{ baseline.name }}</div>
            <div><b>{{ $t('start_date_label') }}:</b> {{ formatDate(baseline.startDate) }}</div>
            <div><b>{{ $t('end_date_label') }}:</b> {{ formatDate(baseline.endDate) }}</div>
            <div v-if="baseline.progress !== undefined">
              <b>{{ $t('progress') }}:</b> {{ formatPercent(baseline.progress) }}
            </div>
          </div>
        </template>
      </template>

      <!-- Baseline Tooltip -->
      <template v-else-if="tooltip.type === 'baseline'">
        <div class="tooltip-title">{{ tooltip.data.row.name }} ({{ tooltip.data.baseline.name }})</div>
        <div><b>{{ $t('start_date_label') }}:</b> {{ formatDate(tooltip.data.baseline.startDate) }}</div>
        <div><b>{{ $t('end_date_label') }}:</b> {{ formatDate(tooltip.data.baseline.endDate) }}</div>
        <div v-if="tooltip.data.baseline.progress !== undefined">
          <b>{{ $t('progress') }}:</b> {{ formatPercent(tooltip.data.baseline.progress) }}
        </div>
      </template>

      <!-- Dependency Tooltip -->
      <template v-else-if="tooltip.type === 'dependency'">
        <div class="tooltip-title">{{ $t('dependency') }}</div>
        <div><b>{{ $t('from') }}:</b> {{ getTaskName(tooltip.data.from) }}</div>
        <div><b>{{ $t('to') }}:</b> {{ getTaskName(tooltip.data.to) }}</div>
        <div><b>{{ $t('type') }}:</b> {{ dependencyTypes[tooltip.data.type] }}</div>
        <div v-if="tooltip.data.lag !== 0">
          <b>Lag:</b> {{ tooltip.data.lag > 0 ? '+' : '' }}{{ tooltip.data.lag }}{{ tooltip.data.lagUnit }}
        </div>
      </template>
    </div>

    <!-- Column Context Menu -->
    <div v-if="columnContextMenu.visible" class="column-context-menu"
      :style="{ left: columnContextMenu.x + 'px', top: columnContextMenu.y + 'px' }">
      <div class="context-menu-title">{{ $t('show_hide_columns') }}</div>
      <div v-for="col in columns" :key="col.field" class="context-menu-item" @click="toggleColumnVisibility(col)">
        <input type="checkbox" :checked="col.visible" />
        <span>{{ $t(col.textKey) || col.text }}</span>
      </div>
    </div>

    <!-- Column Drag Ghost -->
    <div v-if="columnDragging.active && columnDragging.hasMoved"
      class="column-drag-ghost"
      :style="columnGhostStyle">
      <i class="fa fa-grip-vertical column-drag-handle"></i>
      <span class="header-text">{{ visibleColumns[columnDragging.sourceIndex] ? ($t(visibleColumns[columnDragging.sourceIndex].textKey) || visibleColumns[columnDragging.sourceIndex].text) : '' }}</span>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import axios from "axios";
import { mapGetters } from "vuex";
import ganttMixin from './gantt.js';
import { formatProjectFigure, quantityDisplayDecimals } from '·/helpers/quantity-display';

export default {
  name: 'GanttChart',

  mixins: [ganttMixin],

  data() {
    return {
      // Loading
      isLoading: true,

      // Status and progress fields
      statusField: 'status_actual',
      progressField: 'progress_actual',
      isInspected: false,

      // Version counter para invalidar computed properties cuando cambia la data del árbol
      dataVersion: 0,

      // Activity search by planning code
      activitySearchQuery: '',
    };
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
      company: 'app/company'
    }),

    displayDecimals() {
      return quantityDisplayDecimals(this.project)
    },

    // para buscador superior de actividades
    searchableActivities() {
      // eslint-disable-next-line no-unused-expressions
      this.dataVersion;
      return (this.flattenedRows || [])
        .filter(row => row.type === 'activity' && row.code)
        .map(row => ({
          id: row.id,
          code: row.code,
          name: row.name,
          searchLabel: `${row.code} - ${row.name}`,
        }));
    },

    // para buscador superior de actividades
    filteredSearchActivities() {
      const query = (this.activitySearchQuery || '').trim().toLowerCase();
      if (query.length < 1) return [];

      return this.searchableActivities
        .filter(activity => {
          const code = String(activity.code).toLowerCase();
          const name = String(activity.name || '').toLowerCase();
          return code.includes(query) || name.includes(query);
        })
        .slice(0, 50);
    },
  },

  mounted() {
    this.getData();
    document.addEventListener('click', this.handleGlobalClick);
    window.addEventListener('resize', this.calculateContainerHeight);
    this.$nextTick(() => {
      if (this.$refs.rightBody) {
        this.timelineViewportWidth = this.$refs.rightBody.clientWidth;
      }
      this.calculateContainerHeight();
    });
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleGlobalClick);
    window.removeEventListener('resize', this.calculateContainerHeight);
    // Clean up panning listeners in case component unmounts mid-drag
    document.removeEventListener('mousemove', this._boundHandlePan);
    document.removeEventListener('mouseup', this._boundEndPan);
    document.removeEventListener('mousemove', this._boundHandleLeftPan);
    document.removeEventListener('mouseup', this._boundEndLeftPan);
    // Clean up column drag listeners
    document.removeEventListener('mousemove', this.handleColumnDrag);
    document.removeEventListener('mouseup', this.endColumnDrag);
    document.body.classList.remove('column-dragging-active');
    // Clean up column resize listeners
    document.removeEventListener('mousemove', this._boundHandleColumnResize);
    document.removeEventListener('mouseup', this._boundEndColumnResize);
    document.body.classList.remove('column-resizing');
    if (this._colResizeRafId) cancelAnimationFrame(this._colResizeRafId);
    // Limpiamos estilos de panning
    if (this._rafPanId) cancelAnimationFrame(this._rafPanId);
    if (this._rafLeftPanId) cancelAnimationFrame(this._rafLeftPanId);
    if (this._rafSyncId) cancelAnimationFrame(this._rafSyncId);
    if (this._zoomRafId) cancelAnimationFrame(this._zoomRafId);
  },

  methods: {
    formatPercent(value) {
      const formatted = formatProjectFigure(value || 0, this.project)
      return (formatted === '' ? formatProjectFigure(0, this.project) : formatted) + '%'
    },

    // =========================================================================
    // Data loading (API)
    // =========================================================================

    async getData() {
      this.isLoading = true;
      const { data: ganttData } = await axios.get(`/api/v2/project/${this.project.id}/gantt`);

      if (ganttData && ganttData.data && ganttData.data.length) {
        await this.getDependencies()
        this.mappedActivities = ganttData.activities
        this.isInspected = ganttData.is_inspected
        this.criticalPathMode = ganttData.critical_path_mode || 'calculated'
        if (this.isInspected) {
          this.statusField = 'status'
          this.progressField = 'progress'
        }

        let parsedData = this.convertIndexToData(ganttData.data);
        this.rawData = this.mapData(parsedData, this.isGroup(parsedData[0]));
        this.mapTasksDates();
        this.flattenTree();
        this.calculateProjectDates();
        this.calculateEventColors();
        this.dataVersion++;
        this.scheduleDependencyPathsUpdate();
        this.isLoading = false;

        this.$nextTick(() => {
          this.zoomToFit();
          if (this.$refs.rightBody) {
            this.viewportHeight = this.$refs.rightBody.clientHeight;
          }
        });
      }

    },

    async getDependencies() {
      const { data } = await axios.get(`/api/v2/project/${this.project.id}/get-prelations`);
      if (data && data.data) {
        this.dependencies = data.data;
      }
    },

    // =========================================================================
    // Data mapping
    // =========================================================================

    isGroup(item) {
      return item.code !== undefined && !item.planning_code;
    },

    mapData(data, isGroups = false, parent = null, level = 0) {
      const res = [];

      for (const item of data) {
        // if (!item.weight) continue;

        let startDate = item.current_start_date;
        let endDate = item.current_end_date;

        if (!isGroups && this.mappedActivities[item.id]) {
          const activity = this.mappedActivities[item.id];
          // Formato compacto: [start_date, end_date]
          startDate = activity[0];
          endDate = activity[1];
        } else if (!isGroups && startDate && endDate) {
          // Subactividades no procesadas por getProjectGroupsRealDates: aplicar desplazamiento aquí
          const today = new Date().toISOString().slice(0, 10);
          const progress = parseFloat(item[this.progressField]) || 0;
          if (progress === 0 && startDate < today) {
            // No iniciada y atrasada: mover a hoy manteniendo duración
            const dur = Math.round((new Date(endDate) - new Date(startDate)) / 86400000);
            const todayDate = new Date(today);
            startDate = today;
            endDate = new Date(todayDate.getTime() + dur * 86400000).toISOString().slice(0, 10);
          } else if (progress > 0 && progress < 100 && endDate < today) {
            // En progreso y atrasada en fin: estirar hasta hoy
            endDate = today;
          }
        }

        const clean = {
          id: isGroups ? 'G' + item.id : item.id,
          name: item.name,
          code: item.code || item.planning_code,
          status: item[this.statusField] !== undefined ? item[this.statusField] : null,
          critical_path: item.critical_path || false,
          parentId: parent ? parent.id : null,
          parent_activity_id: isGroups ? null : item.parent_id,
          percentDone: parseFloat(item[this.progressField]) || 0,
          latestPlanningProgress: this.calculateProgress(item.current_start_date, item.current_end_date),
          startDate,
          endDate,
          type: isGroups ? 'group' : 'activity',
          // group | activity | subactivity
          kind: isGroups ? 'group' : (parent && parent.type === 'activity' ? 'subactivity' : 'activity'),
          // Hereda el color del padre
          groupColor: (isGroups && item.color) || (parent && parent.groupColor) || this.colors.defaults.group,
          baselines: [],
          expanded: false,
          level,
          visible: level === 0,
          weight: item.weight,
          gantt_real_start_date: item.gantt_real_start_date,
          gantt_real_end_date: item.gantt_real_end_date,
          current_start_date: item.current_start_date,
          current_end_date: item.current_end_date,
          scheduled_start_date: item.scheduled_start_date,
          scheduled_end_date: item.scheduled_end_date,
          isCriticalPath: false,
          eventColor: this.colors.defaults.scheduled.light,
          children: []
        };

        if (isGroups) {
          clean.groupInitials = (item.name || '').substring(0, 2).toUpperCase();
          clean.groupTextColor = this.getBadgeTextColor(clean.groupColor);
        }

        // Map children — un grupo puede tener tanto subgrupos como actividades directas
        if (item.children_groups?.length && item.activities?.length) {
          clean.children = [
            ...this.mapData(item.children_groups, true, clean, level + 1),
            ...this.mapData(item.activities, false, clean, level + 1)
          ];
        } else if (item.children_groups?.length) {
          clean.children = this.mapData(item.children_groups, true, clean, level + 1);
        } else if (item.activities?.length) {
          clean.children = this.mapData(item.activities, false, clean, level + 1);
        } else if (item.children_activities?.length) {
          clean.children = this.mapData(item.children_activities, false, clean, level + 1);
        }

        // Setup baselines. Tomamos el nombre de baselinesConfig para que el tooltip
        // coincida siempre con el desplegable Views (una sola fuente de verdad).
        clean.baselines = [
          {
            name: this.$t(this.baselinesConfig['baseline-0'].labelKey),
            startDate: item.scheduled_start_date,
            endDate: item.scheduled_end_date,
            progress: this.calculateProgress(item.scheduled_start_date, item.scheduled_end_date),
            index: 0
          },
          {
            name: this.$t(this.baselinesConfig['baseline-1'].labelKey),
            startDate: item.current_start_date,
            endDate: item.current_end_date,
            progress: this.calculateProgress(item.current_start_date, item.current_end_date),
            index: 1
          },
          {
            name: this.$t(this.baselinesConfig['baseline-2'].labelKey),
            startDate: clean.startDate,
            endDate: clean.endDate,
            progress: clean.percentDone,
            index: 2
          }
        ];

        res.push(clean);
      }

      return res;
    },

    convertIndexToData(data, type = 'group') {
      const types = {
        'activity': {
          0: 'id',
          1: 'planning_code',
          2: 'name',
          3: 'parent_id',
          4: 'group_id',
          5: 'critical_path',
          6: 'weight',
          7: 'progress',
          8: 'progress_actual',
          9: 'status',
          10: 'status_actual',
          11: 'current_start_date',
          12: 'current_end_date',
          13: 'scheduled_start_date',
          14: 'scheduled_end_date',
          15: 'gantt_real_start_date',
          16: 'gantt_real_end_date',
          17: 'children_activities'
        },
        'group': {
          0: 'id',
          1: 'code',
          2: 'name',
          3: 'weight',
          4: 'parent_id',
          5: 'activities',
          6: 'children_groups',
          7: 'color'
        }
      };

      const typeMap = types[type] || {};
      const result = [];

      if (!Array.isArray(data)) {
        return [];
      }

      for (const item of data) {
        const mapped = {};
        for (const [index, key] of Object.entries(typeMap)) {
          const idx = parseInt(index);
          if (key === 'activities' || key === 'children_activities') {
          mapped[key] = item[idx] ? this.convertIndexToData(item[idx], 'activity') : [];
          continue;
          } else if (key === 'children_groups') {
          mapped[key] = item[idx] ? this.convertIndexToData(item[idx], 'group') : [];
          continue;
          }
          mapped[key] = item[idx] !== undefined ? item[idx] : null;
        }
        result.push(mapped);
      }

      return result;
    },

    // =========================================================================
    // Date calculations (recursive)
    // =========================================================================

    // El API devuelve decimal como string ("0.00000" es truthy y no se puede sumar).
    numericWeight(value) {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    // Progreso de línea base de un padre/grupo: promedio del progreso de la misma
    // línea base de sus hijos, ponderado por su peso. Los de peso 0/null no cuentan.
    weightedBaselineProgress(children, index) {
      if (!children || !children.length) return 0;
      let totalProgress = 0;
      let totalWeight = 0;
      for (const child of children) {
        const weight = this.numericWeight(child.weight);
        if (!weight) continue;
        const baseline = child.baselines && child.baselines[index];
        if (!baseline) continue;
        totalProgress += (parseFloat(baseline.progress) || 0) * weight;
        totalWeight += weight;
      }
      return totalWeight > 0 ? parseFloat((totalProgress / totalWeight).toFixed(5)) : 0;
    },

    mapTasksDates() {
      for (let treeNode of this.rawData) {
        this.updateDatesByChildren(treeNode);
      }
    },

    updateDatesByChildren(node) {
      // Primero actualizamos hijos recursivamente
      if (node.children && node.children.length) {
        node.children.forEach(child => this.updateDatesByChildren(child));
      }

      if (!node.children || !node.children.length) return;

      // Progreso ponderado. El peso llega como string decimal ("33.33000");
      // sumarlo con += concatena y el promedio no se aplica.
      let totalProgress = 0;
      let totalWeight = 0;
      for (const child of node.children) {
        const weight = this.numericWeight(child.weight);
        totalProgress += (parseFloat(child.percentDone) || 0) * weight;
        totalWeight += weight;
      }
      if (totalWeight > 0) {
        node.percentDone = parseFloat((totalProgress / totalWeight).toFixed(5));
      }

      // Min/max fechas usando comparación de strings (YYYY-MM-DD es lexicográficamente ordenable)
      let minStart = null;
      let maxEnd = null;
      for (const child of node.children) {
        // Las de peso 0/null son fantasma: se ven pero no arrastran las fechas del grupo
        if (!this.numericWeight(child.weight)) continue;
        if (child.startDate && (!minStart || child.startDate < minStart)) minStart = child.startDate;
        if (child.endDate && (!maxEnd || child.endDate > maxEnd)) maxEnd = child.endDate;
      }
      if (minStart) node.startDate = minStart;
      if (maxEnd) node.endDate = maxEnd;

      // Actualizar baseline inspected (index 2)
      if (node.baselines && node.baselines[2]) {
        node.baselines[2].startDate = node.startDate;
        node.baselines[2].endDate = node.endDate;
        node.baselines[2].progress = node.percentDone;
      }

      // El progreso de las líneas base de padres y grupos se escala por ponderación de sus
      // hijos, no sobre la fecha actual. En hojas se conserva el avance por fecha de mapData.
      if (node.baselines && node.baselines[0]) {
        node.baselines[0].progress = this.weightedBaselineProgress(node.children, 0);
      }
      if (node.baselines && node.baselines[1]) {
        node.baselines[1].progress = this.weightedBaselineProgress(node.children, 1);
        node.latestPlanningProgress = node.baselines[1].progress;
      }

      // Para grupos, actualizar fechas scheduled y current
      if (node.type === 'group') {
        let minScheduledStart = null;
        let maxScheduledEnd = null;
        let minCurrentStart = null;
        let maxCurrentEnd = null;

        for (const child of node.children) {
          // Mismo criterio fantasma: las de peso 0/null no mueven las fechas scheduled/current
          if (!this.numericWeight(child.weight)) continue;
          if (child.scheduled_start_date && (!minScheduledStart || child.scheduled_start_date < minScheduledStart))
            minScheduledStart = child.scheduled_start_date;
          if (child.scheduled_end_date && (!maxScheduledEnd || child.scheduled_end_date > maxScheduledEnd))
            maxScheduledEnd = child.scheduled_end_date;
          if (child.current_start_date && (!minCurrentStart || child.current_start_date < minCurrentStart))
            minCurrentStart = child.current_start_date;
          if (child.current_end_date && (!maxCurrentEnd || child.current_end_date > maxCurrentEnd))
            maxCurrentEnd = child.current_end_date;
        }

        if (minScheduledStart) node.scheduled_start_date = minScheduledStart;
        if (maxScheduledEnd) node.scheduled_end_date = maxScheduledEnd;
        if (minCurrentStart) node.current_start_date = minCurrentStart;
        if (maxCurrentEnd) node.current_end_date = maxCurrentEnd;

        // Solo fechas: el progreso de estas líneas base ya se calculó por ponderación de hijos arriba.
        if (node.baselines && node.baselines[0]) {
          node.baselines[0].startDate = node.scheduled_start_date;
          node.baselines[0].endDate = node.scheduled_end_date;
        }
        if (node.baselines && node.baselines[1]) {
          node.baselines[1].startDate = node.current_start_date;
          node.baselines[1].endDate = node.current_end_date;
        }

        // Calcular gantt real dates para grupos
        const ganttDates = this.getGanttRealDates(node);
        node.gantt_real_start_date = ganttDates.gantt_real_start_date;
        if (node.percentDone >= 100) {
          node.gantt_real_end_date = ganttDates.gantt_real_end_date;
        }
      }
    },

    getGanttRealDates(item) {
      let minStart = null;
      let maxEnd = null;

      const findDatesInChildren = (children) => {
        for (const child of children) {
          // Fantasma: ni la actividad de peso 0/null ni su subárbol cuentan para las fechas reales
          if (!this.numericWeight(child.weight)) continue;
          if (child.gantt_real_start_date) {
            if (!minStart || child.gantt_real_start_date < minStart) {
              minStart = child.gantt_real_start_date;
            }
          }
          if (child.percentDone >= 100 && child.gantt_real_end_date) {
            if (!maxEnd || child.gantt_real_end_date > maxEnd) {
              maxEnd = child.gantt_real_end_date;
            }
          }
          if (child.children && child.children.length) {
            findDatesInChildren(child.children);
          }
        }
      };

      if (item.children && item.children.length) {
        findDatesInChildren(item.children);
      }

      return {
        gantt_real_start_date: minStart || null,
        gantt_real_end_date: maxEnd || null
      };
    },

    // =========================================================================
    // Tree flattening
    // =========================================================================

    flattenTree() {
      this.flattenedRows = [];
      this.flattenNode(this.rawData, 0, true);
    },

    flattenNode(nodes, level, parentVisible) {
      for (const node of nodes) {
        node.level = level;
        node.visible = parentVisible;
        this.flattenedRows.push(node);

        if (node.children && node.children.length) {
          this.flattenNode(node.children, level + 1, parentVisible && node.expanded);
        }
      }
    },

    // =========================================================================
    // Project date calculations
    // =========================================================================

    calculateProjectDates() {
      // Usamos comparación de strings para encontrar min/max sin crear moment objects
      let minDateStr = null;
      let maxDateStr = null;

      for (const row of this.flattenedRows) {
        if (row.startDate && (!minDateStr || row.startDate < minDateStr)) minDateStr = row.startDate;
        if (row.endDate && (!maxDateStr || row.endDate > maxDateStr)) maxDateStr = row.endDate;
      }

      this.projectStartDate = minDateStr ? moment(minDateStr) : null;
      this.projectEndDate = maxDateStr ? moment(maxDateStr) : null;

      this.timelineStartDate = this.projectStartDate ? moment(this.projectStartDate).subtract(3, 'years') : moment().subtract(3, 'years');
      this.timelineEndDate = this.projectEndDate ? moment(this.projectEndDate).add(3, 'years') : moment().add(3, 'years');
    },

    // =========================================================================
    // Critical Path Method (CPM)
    // =========================================================================

    calculateCriticalPaths() {
      // 1. Reset
      for (const row of this.flattenedRows) {
        row.isCriticalPath = false;
        row.earlyStart = null;
        row.earlyFinish = null;
        row.lateStart = null;
        row.lateFinish = null;
        row.totalFloat = null;
      }

      // 2. Actividades con fechas
      const allWithDates = this.flattenedRows.filter(row => row.startDate && row.endDate);
      const leafTasks = allWithDates.filter(row => !row.children || row.children.length === 0);
      if (allWithDates.length === 0) return;

      // 3. Modo de camino crítico determinado por el backend
      if (this.criticalPathMode === 'p6') {
        // MODO P6: todas las actividades con critical_path (float=0 en P6) se marcan como críticas
        for (const task of allWithDates) {
          task.isCriticalPath = !!task.critical_path;
          task.totalFloat = task.critical_path ? 0 : null;
        }

      } else {
        // MODO CALCULADO: lógica original del gantt
        const MS_PER_DAY = 86400000;
        if (leafTasks.length === 0) return;

        const taskMap = new Map();
        for (const task of leafTasks) taskMap.set(task.id, task);

        // Dependencias
        const successors = new Map();
        const predecessors = new Map();
        for (const dep of this.dependencies) {
          if (!taskMap.has(dep.from) || !taskMap.has(dep.to)) continue;
          if (!successors.has(dep.from)) successors.set(dep.from, []);
          if (!predecessors.has(dep.to)) predecessors.set(dep.to, []);
          successors.get(dep.from).push(dep);
          predecessors.get(dep.to).push(dep);
        }

        // Duración inclusiva (+1 día, como MS Project)
        const getDurationDays = (task) => {
          if (!task.startDate || !task.endDate) return 0;
          return Math.max(0, Math.round((new Date(task.endDate) - new Date(task.startDate)) / MS_PER_DAY) + 1);
        };

        const getLagDays = (dep) => {
          const lag = dep.lag || 0;
          switch (dep.lagUnit) {
            case 'w': return lag * 7;
            case 'M': return lag * 30;
            case 'y': return lag * 365;
            default: return lag;
          }
        };

        // Forward pass: ES/EF desde fechas display
        for (const task of leafTasks) {
          task.earlyStart = new Date(task.startDate).getTime();
          task.earlyFinish = task.earlyStart + (getDurationDays(task) * MS_PER_DAY);
        }

        // Project end
        let projectEnd = 0;
        for (const task of leafTasks) {
          if (task.earlyFinish > projectEnd) projectEnd = task.earlyFinish;
        }

        // Backward pass
        const calcLate = (task, visited = new Set()) => {
          if (visited.has(task.id)) return task.lateStart;
          if (task.lateFinish !== null) return task.lateStart;
          visited.add(task.id);

          const succs = successors.get(task.id) || [];
          let minLF = projectEnd;

          for (const dep of succs) {
            const succTask = taskMap.get(dep.to);
            if (!succTask) continue;
            calcLate(succTask, visited);
            if (succTask.lateStart === null) continue;

            const lagMs = getLagDays(dep) * MS_PER_DAY;
            const durMs = getDurationDays(task) * MS_PER_DAY;
            let cf;
            switch (dep.type) {
              case 0: cf = succTask.lateStart - lagMs + durMs; break;
              case 1: cf = succTask.lateFinish - lagMs + durMs; break;
              case 3: cf = succTask.lateFinish - lagMs; break;
              case 2: default: cf = succTask.lateStart - lagMs; break;
            }
            if (cf < minLF) minLF = cf;
          }

          task.lateFinish = minLF;
          task.lateStart = minLF - (getDurationDays(task) * MS_PER_DAY);
          return task.lateStart;
        };

        for (const task of leafTasks) calcLate(task);

        // Float
        const EPSILON = 1000;
        const zeroFloat = new Set();
        for (const task of leafTasks) {
          if (task.earlyStart !== null && task.lateStart !== null) {
            task.totalFloat = task.lateStart - task.earlyStart;
            if (Math.abs(task.totalFloat) <= EPSILON) zeroFloat.add(task.id);
          }
        }

        // Longest Path: entre las float=0, encontrar la cadena conectada más larga
        if (zeroFloat.size > 0) {
          const zfPreds = new Map();
          for (const dep of this.dependencies) {
            if (!zeroFloat.has(dep.from) || !zeroFloat.has(dep.to)) continue;
            if (!zfPreds.has(dep.to)) zfPreds.set(dep.to, []);
            zfPreds.get(dep.to).push(dep);
          }

          const pathDur = new Map();
          const drivingPred = new Map();
          const gpl = (id, vis = new Set()) => {
            if (pathDur.has(id)) return pathDur.get(id);
            if (vis.has(id)) return getDurationDays(taskMap.get(id));
            vis.add(id);
            let mx = 0, bp = null;
            for (const dep of (zfPreds.get(id) || [])) {
              const pl = gpl(dep.from, vis);
              if (pl > mx) { mx = pl; bp = dep.from; }
            }
            const total = mx + getDurationDays(taskMap.get(id));
            pathDur.set(id, total);
            if (bp) drivingPred.set(id, bp);
            return total;
          };
          for (const id of zeroFloat) gpl(id);

          let maxDur = 0;
          for (const [, d] of pathDur) { if (d > maxDur) maxDur = d; }

          const critSet = new Set();
          for (const [id, d] of pathDur) {
            if (d === maxDur) {
              let c = id;
              while (c) { critSet.add(c); c = drivingPred.get(c) || null; }
            }
          }

          for (const task of leafTasks) {
            task.isCriticalPath = critSet.has(task.id);
          }
        }
      }

      // Propagar a padres
      const markParentsCritical = (node) => {
        if (node.children && node.children.length) {
          for (const child of node.children) {
            if (markParentsCritical(child)) node.isCriticalPath = true;
          }
        }
        return node.isCriticalPath;
      };
      for (const root of this.rawData) markParentsCritical(root);
    },

    // =========================================================================
    // Event colors
    // =========================================================================

    calculateEventColors() {
      this.calculateCriticalPaths();

      const setColor = (node) => {
        node.eventColor = this.colors.defaults.scheduled.normal;

        if (parseInt(node.percentDone) === 0) {
          node.eventColor = this.colors.defaults.gray.normal;
        } else if (node.type === 'activity') {
          if (parseInt(node.percentDone) > 0 && !node.gantt_real_start_date) {
            node.eventColor = this.colors.defaults.yellow.normal;
          } else if (parseInt(node.percentDone) === 100 && !node.gantt_real_end_date) {
            node.eventColor = this.colors.defaults.yellow.normal;
          }
        }

        if (node.children && node.children.length) {
          node.children.forEach(setColor);

          if (node.children.some(c => c.eventColor === this.colors.defaults.yellow.normal)) {
            node.eventColor = this.colors.defaults.yellow.normal;
          } else if (node.children.some(c => c.eventColor === this.colors.defaults.scheduled.normal)) {
            node.eventColor = this.colors.defaults.scheduled.normal;
          }
        }

        if (node.isCriticalPath && this.enableCriticalPaths) {
          node.eventColor = this.colors.defaults.critical.normal;
        }
      };

      this.rawData.forEach(setColor);
    },

    calculateProgress(startDate, endDate) {
      if (!startDate || !endDate) return 0;

      const start = moment(startDate);
      const end = moment(endDate);
      const now = moment();

      if (now.isBefore(start)) return 0;
      if (now.isSameOrAfter(end)) return 100;

      const totalDuration = end.diff(start, 'milliseconds');
      const elapsedDuration = now.diff(start, 'milliseconds');
      return parseFloat(((elapsedDuration / totalDuration) * 100).toFixed(5));
    },

    // =========================================================================
    // Critical paths toggle
    // =========================================================================

    toggleCriticalPaths() {
      this.enableCriticalPaths = !this.enableCriticalPaths;
      if (!this.enableCriticalPaths) {
        this.filterCriticalOnly = false;
        this.filterCriticalFlat = false;
        this.filterLongestPath = false;
        this.showCriticalMenu = false;
      }
      this.calculateEventColors();
      this.dataVersion++;
      this.visibleVersion++;
      this.scheduleDependencyPathsUpdate();
    },

    setCriticalFilter(mode) {
      if (mode === 'longestPath') {
        this.filterLongestPath = !this.filterLongestPath;
        if (this.filterLongestPath) this.filterCriticalFlat = false;
      } else if (mode === 'flat') {
        this.filterCriticalFlat = !this.filterCriticalFlat;
        if (this.filterCriticalFlat) this.filterLongestPath = false;
      }
      this.visibleVersion++;
      this.scheduleDependencyPathsUpdate();
    },

    // =========================================================================
    // Navigation (project-specific)
    // =========================================================================

    getActivityUrl(row) {
      if (row.type !== 'activity') return '#';
      var route;
      if (row.parent_activity_id) {
        route = this.$router.resolve({
          name: 'subactivity.home',
          params: { activity_id: row.parent_activity_id, subactivity_id: row.id }
        });
      } else {
        route = this.$router.resolve({
          name: 'activity.home',
          params: { activity_id: row.id }
        });
      }
      return route.href;
    },

    onActivitySearchSelect(option) {
      if (!option) return;

      if (this.filterCriticalFlat || this.filterLongestPath || this.filterCriticalOnly) {
        this.filterCriticalFlat = false;
        this.filterLongestPath = false;
        this.filterCriticalOnly = false;
      }

      this.goToTask(option.id);
      this.activitySearchQuery = option.code;
    },

    goToTask(taskId) {
      const task = this.taskMap[taskId];
      if (!task) return;

      const ancestors = [];
      let current = task;
      while (current && current.parentId) {
        const parent = this.taskMap[current.parentId];
        if (parent) ancestors.unshift(parent);
        current = parent;
      }

      // Expand all parents
      for (const parent of ancestors) {
        if (parent.expanded || !parent.children || !parent.children.length) continue;

        this.expandedParentIds.push(parent.id);
        while (this.expandedParentIds.length > this.maxExpandedParents) {
          const oldestId = this.expandedParentIds.shift();
          const oldestRow = this.taskMap[oldestId];
          if (oldestRow) oldestRow.expanded = false;
        }
        parent.expanded = true;
      }

      this.updateVisibility();
      this.selectedRowId = taskId;

      this.$nextTick(() => {
        this.scrollToTask(task);
      });
    },
  }
};
</script>

<style>
  /**
 * Global styles (unscoped)
 * These must be global because they affect the <body> element during drag operations.
 */

  /* Splitter dragging */
  body.splitter-dragging,
  body.splitter-dragging * {
    cursor: col-resize !important;
    user-select: none !important;
  }

  /* Panning */
  /* Los estilos de panning se aplican vía JS directo en los elementos para evitar style recalc global */

  /* Critical path: botón + caret como uno solo */
  .critical-path-dropdown {
    position: relative;
    display: inline-flex !important;
  }

  .critical-path-btn {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    margin-right: 0 !important;
  }

  .critical-path-caret {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-left: 1px solid rgba(255,255,255,0.25) !important;
    padding: 4px 7px !important;
    margin-left: 0 !important;
    min-width: auto !important;
  }

  .critical-path-caret:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Dropdown rojo para critical path */
  .gantt-dropdown-menu.critical-dropdown-menu .dropdown-item input[type="checkbox"]:checked {
    background: #e3342f !important;
    border-color: #e3342f !important;
  }

  .gantt-dropdown-menu.critical-dropdown-menu .dropdown-item input[type="checkbox"]:hover {
    border-color: #e3342f !important;
  }

  .gantt-dropdown-menu.critical-dropdown-menu .dropdown-item:hover {
    background: rgba(227, 52, 47, 0.08) !important;
  }

  /* Column dragging */
  body.column-dragging-active {
    cursor: grabbing !important;
    user-select: none !important;
  }

  body.column-dragging-active * {
    cursor: grabbing !important;
  }

  /* Column resizing */
  body.column-resizing {
    cursor: col-resize !important;
    user-select: none !important;
  }

  body.column-resizing * {
    cursor: col-resize !important;
  }
</style>

<style scoped src="./gantt.css"></style>
